import React, { useEffect, useMemo, useRef, useState } from "react";
import AppLayout from "../components/AppLayout";
import TestCloseButton from "../components/TestCloseButton";
import IQFigure from "../components/IQFigure";
import { api, formatApiErrorDetail } from "../lib/api";
import {
  Lightbulb, Hourglass, Clock, ArrowRight, ArrowLeft, CheckCircle, WarningCircle,
} from "@phosphor-icons/react";

const CATEGORY_LABEL = {
  verbaal: "Verbaal",
  numeriek: "Numeriek",
  logisch: "Logisch",
  ruimtelijk: "Ruimtelijk",
};

export default function CoacheeIQTestPage() {
  const [coachee, setCoachee] = useState(null);
  const [session, setSession] = useState(null);
  const [index, setIndex] = useState(0);
  const [remaining, setRemaining] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const tickRef = useRef(null);
  const submittedRef = useRef(false);

  // Load coachee + current session on mount.
  useEffect(() => {
    (async () => {
      try {
        const { data: u } = await api.get("/auth/me");
        const { data: list } = await api.get("/coachees");
        const own = list.find((p) => p.user_id === u.id) || null;
        setCoachee(own);
        if (own) {
          const { data: s } = await api.get(`/coachees/${own.id}/iq-test`);
          setSession(s);
          setRemaining(s.remaining_seconds || 0);
        }
      } catch (err) {
        setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Countdown timer
  useEffect(() => {
    if (!session || session.state !== "in_progress") {
      if (tickRef.current) clearInterval(tickRef.current);
      return;
    }
    tickRef.current = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) {
          if (tickRef.current) clearInterval(tickRef.current);
          handleSubmit(true);
          return 0;
        }
        return r - 1;
      });
    }, 1000);
    return () => { if (tickRef.current) clearInterval(tickRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.state]);

  const items = session?.items || [];
  const answers = session?.answers || {};
  const total = items.length;
  const currentItem = items[index];
  const answered = Object.keys(answers).length;

  const handleStart = async () => {
    if (!coachee || busy) return;
    setBusy(true); setError("");
    try {
      const { data } = await api.post(`/coachees/${coachee.id}/iq-test/start`);
      setSession(data);
      setRemaining(data.remaining_seconds || 0);
      setIndex(0);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const handlePick = async (choice) => {
    if (!coachee || !currentItem || busy) return;
    // optimistic update
    setSession((prev) => ({
      ...prev,
      answers: { ...(prev?.answers || {}), [currentItem.id]: choice },
    }));
    try {
      const { data } = await api.put(`/coachees/${coachee.id}/iq-test/answer`, {
        item_id: currentItem.id,
        choice,
      });
      setSession((prev) => ({ ...prev, ...data, items: prev.items }));
      setRemaining(data.remaining_seconds || 0);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const handleSubmit = async (auto = false) => {
    if (!coachee || submittedRef.current) return;
    submittedRef.current = true;
    setBusy(true); setError("");
    try {
      const { data } = await api.post(`/coachees/${coachee.id}/iq-test/submit`);
      setSession(data);
    } catch (err) {
      submittedRef.current = false;
      if (!auto) setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <AppLayout title="Klassieke IQ-test"><div className="text-zinc-500">Laden...</div></AppLayout>;
  }
  if (error && !session) {
    return (
      <AppLayout title="Klassieke IQ-test" breadcrumbs={[{ label: "Tests", to: "/tests" }, { label: "IQ-test" }]}>
        <ErrorBox text={error} />
      </AppLayout>
    );
  }
  if (session?.state === "completed" && session?.result) {
    return <ResultView session={session} />;
  }
  if (session?.state === "in_progress" && currentItem) {
    return (
      <ActiveTestView
        items={items}
        index={index}
        setIndex={setIndex}
        currentItem={currentItem}
        answers={answers}
        answered={answered}
        total={total}
        remaining={remaining}
        onPick={handlePick}
        onSubmit={() => handleSubmit(false)}
        busy={busy}
        error={error}
      />
    );
  }
  // not_started
  return (
    <IntroView onStart={handleStart} busy={busy} error={error} />
  );
}

// ---------------------------------------------------------------------------
// Intro screen
// ---------------------------------------------------------------------------
function IntroView({ onStart, busy, error }) {
  return (
    <AppLayout title="Klassieke IQ-test" breadcrumbs={[{ label: "Tests", to: "/tests" }, { label: "IQ-test" }]}>
      <div className="max-w-2xl space-y-6" data-testid="iq-intro">
        <div className="neuro-card-bold p-6 bg-white">
          <div className="flex items-start gap-3">
            <div className="shrink-0 inline-flex h-12 w-12 items-center justify-center bg-[#1E88E5]/10 text-[#1E88E5] rounded-sm">
              <Lightbulb size={26} weight="duotone" />
            </div>
            <div className="flex-1">
              <h2 className="font-display text-2xl font-bold tracking-tight">Klassieke IQ-test</h2>
              <p className="text-sm text-zinc-600 mt-2 leading-relaxed">
                Een korte indicatieve test van <strong>30 vragen</strong> over 4 categorieën:
                verbaal, numeriek, logisch en ruimtelijk inzicht.
              </p>
              <ul className="text-sm text-zinc-600 mt-3 space-y-1.5 list-disc list-inside">
                <li>Je krijgt <strong>25 minuten</strong> de tijd.</li>
                <li>De klok loopt door, ook als je het tabblad sluit.</li>
                <li>Wanneer de tijd om is, wordt je test automatisch beoordeeld.</li>
                <li>Je krijgt een geschat IQ met een <strong>95% betrouwbaarheidsinterval</strong>.</li>
                <li>De test kan maar één keer gemaakt worden — vraag je work coach om reset als je wilt herkansen.</li>
              </ul>
              <p className="text-xs text-zinc-500 mt-4 italic">
                Dit is een indicatieve test, geen formeel psychodiagnostisch instrument.
                Voor een officiële IQ-meting verwijzen we je naar een gecertificeerd psycholoog.
              </p>
            </div>
          </div>
        </div>
        {error && <ErrorBox text={error} />}
        <button
          type="button"
          onClick={onStart}
          disabled={busy}
          data-testid="iq-start-btn"
          className="inline-flex items-center gap-2 bg-zinc-900 text-white px-5 py-2.5 text-sm font-semibold tracking-wide uppercase rounded-sm hover:bg-[#1E88E5] transition-colors disabled:bg-zinc-400"
        >
          {busy ? "Bezig..." : "Start de test"} <ArrowRight size={16} />
        </button>
      </div>
    </AppLayout>
  );
}

// ---------------------------------------------------------------------------
// Active test screen
// ---------------------------------------------------------------------------
function ActiveTestView({
  items, index, setIndex, currentItem, answers, answered, total,
  remaining, onPick, onSubmit, busy, error,
}) {
  const currentAnswer = answers[currentItem.id];
  const isLast = index === total - 1;
  return (
    <AppLayout title="Klassieke IQ-test">
      <div className="max-w-3xl space-y-4" data-testid="iq-active">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="text-xs uppercase tracking-widest text-zinc-500">
            Vraag <strong className="text-zinc-900">{index + 1}</strong> / {total}
            <span className="mx-2 text-zinc-300">·</span>
            <span data-testid="iq-category">{CATEGORY_LABEL[currentItem.category] || currentItem.category}</span>
          </div>
          <CountdownBadge seconds={remaining} />
        </div>

        <div className="h-1 bg-zinc-100">
          <div className="h-full bg-zinc-900 transition-all duration-300" style={{ width: `${(index / total) * 100}%` }} />
        </div>

        <div className="neuro-card-bold p-6 bg-white" data-testid="iq-question-card">
          <h2 className="font-display text-xl md:text-2xl font-bold tracking-tight leading-snug">
            {currentItem.question}
          </h2>
          {currentItem.figure && <IQFigure figureId={currentItem.figure} />}
          <div className="mt-6 space-y-2">
            {currentItem.options.map((opt, i) => (
              <button
                key={`${currentItem.id}-${i}`}
                type="button"
                onClick={() => onPick(i)}
                data-testid={`iq-option-${i}`}
                className={
                  "block w-full text-left p-4 border-2 rounded-sm transition-all " +
                  (currentAnswer === i
                    ? "border-[#1E88E5] bg-[#1E88E5]/5"
                    : "border-zinc-200 hover:border-zinc-400 bg-white")
                }
              >
                <span className="text-[10px] uppercase tracking-widest text-zinc-500 mr-2">
                  {String.fromCharCode(65 + i)}
                </span>
                <span className="font-medium text-[15px]">{opt}</span>
              </button>
            ))}
          </div>
        </div>

        {error && <ErrorBox text={error} />}

        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={() => setIndex(Math.max(0, index - 1))}
            disabled={index === 0}
            data-testid="iq-prev"
            className="inline-flex items-center gap-2 text-sm font-medium text-zinc-600 hover:text-zinc-900 disabled:opacity-30"
          >
            <ArrowLeft size={16} /> Vorige
          </button>
          <div className="text-xs text-zinc-400" data-testid="iq-answered-count">
            {answered} van {total} beantwoord
          </div>
          {!isLast ? (
            <button
              type="button"
              onClick={() => setIndex(Math.min(total - 1, index + 1))}
              data-testid="iq-next"
              className="inline-flex items-center gap-2 bg-zinc-900 text-white px-5 py-2 text-sm font-semibold tracking-wide uppercase rounded-sm hover:bg-[#1E88E5] transition-colors"
            >
              Volgende <ArrowRight size={16} />
            </button>
          ) : (
            <button
              type="button"
              onClick={onSubmit}
              disabled={busy}
              data-testid="iq-submit"
              className="inline-flex items-center gap-2 bg-[#43A047] text-white px-5 py-2 text-sm font-semibold tracking-wide uppercase rounded-sm hover:bg-[#388e3c] transition-colors disabled:bg-zinc-400"
            >
              {busy ? "Versturen..." : "Voltooien"} <CheckCircle size={16} />
            </button>
          )}
        </div>
      </div>
    </AppLayout>
  );
}

// ---------------------------------------------------------------------------
// Result screen
// ---------------------------------------------------------------------------
function ResultView({ session }) {
  const r = session.result || {};
  const dist = useMemo(() => r.distribution || [], [r.distribution]);
  const maxP = useMemo(() => dist.reduce((m, p) => Math.max(m, p.p), 0) || 1, [dist]);

  return (
    <AppLayout title="Klassieke IQ-test" breadcrumbs={[{ label: "Tests", to: "/tests" }, { label: "IQ-test" }]}>
      <div className="max-w-3xl space-y-6" data-testid="iq-result">
        <div className="neuro-card-bold p-8 bg-white">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Jouw resultaat</div>
          <div className="mt-2 flex items-end gap-3 flex-wrap">
            <div className="font-display text-5xl md:text-6xl font-bold tracking-tight text-zinc-900" data-testid="iq-result-score">
              {Math.round(r.iq_mean)}
            </div>
            <div className="text-sm text-zinc-500 pb-2" data-testid="iq-result-ci">
              95% betrouwbaarheidsinterval: <strong className="text-zinc-900">{r.iq_ci_low} – {r.iq_ci_high}</strong>
            </div>
          </div>
          <p className="text-sm text-zinc-600 mt-4 leading-relaxed">
            Je hebt <strong>{r.correct}</strong> van de <strong>{r.answered}</strong> beantwoorde vragen goed. Dit
            resultaat is berekend met Bayesiaanse statistiek: we starten bij een
            gemiddelde van 100 (de standaardverdeling van IQ) en passen die schatting
            aan op basis van de moeilijkheidsgraad van de vragen die je goed en fout had.
          </p>
          <p className="text-xs text-zinc-500 mt-3 italic">
            Het 95%-interval geeft aan tussen welke waarden je echte IQ met grote
            waarschijnlijkheid ligt. Een nauwer interval betekent meer zekerheid; meer
            vragen beantwoorden geeft een nauwer interval.
          </p>
        </div>

        {dist.length > 0 && (
          <div className="neuro-card p-6 bg-white">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-3">
              Posterior­verdeling
            </div>
            <div className="flex items-end gap-[2px] h-32" data-testid="iq-result-chart">
              {dist.map((d) => {
                const h = Math.max(2, Math.round((d.p / maxP) * 100));
                const inCI = d.iq >= r.iq_ci_low && d.iq <= r.iq_ci_high;
                return (
                  <div
                    key={d.iq}
                    title={`IQ ${d.iq}`}
                    style={{ height: `${h}%` }}
                    className={"flex-1 rounded-t-sm " + (inCI ? "bg-[#1E88E5]" : "bg-zinc-300")}
                  />
                );
              })}
            </div>
            <div className="flex justify-between text-[10px] text-zinc-400 mt-1">
              <span>{dist[0]?.iq}</span>
              <span>100</span>
              <span>{dist[dist.length - 1]?.iq}</span>
            </div>
            <div className="text-xs text-zinc-500 mt-3">
              De blauwe staafjes geven het 95% betrouwbaarheidsinterval weer.
            </div>
          </div>
        )}

        <TestCloseButton testId="iq-result-back" />
      </div>
    </AppLayout>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function CountdownBadge({ seconds }) {
  const m = Math.floor(Math.max(0, seconds) / 60);
  const s = Math.max(0, seconds) % 60;
  const lowTime = seconds < 120;
  return (
    <div
      className={
        "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm border " +
        (lowTime
          ? "bg-[#E53935]/10 text-[#E53935] border-[#E53935]/30"
          : "bg-zinc-900 text-white border-zinc-900")
      }
      data-testid="iq-timer"
    >
      <Clock size={14} weight="duotone" />
      <span className="font-mono text-sm tabular-nums">
        {m}:{String(s).padStart(2, "0")}
      </span>
    </div>
  );
}

function ErrorBox({ text }) {
  return (
    <div
      className="border border-[#E53935]/30 bg-[#E53935]/5 text-[#E53935] text-sm px-4 py-3 rounded-sm flex items-start gap-2"
      data-testid="iq-error"
    >
      <WarningCircle size={18} className="shrink-0 mt-0.5" />
      <div>{text}</div>
    </div>
  );
}
